/**
 * A per-document collaboration session: one Y.Doc + one WebrtcProvider +
 * the highlights bridge, with a deterministic room derived from the project key.
 *
 * The durable store is unchanged — the encrypted document.json snapshot (and
 * highlights.json) written by autosave. This session is the *live* layer:
 *  - The Y.Doc is RESTORED from the snapshot's persisted Yjs state
 *    (QdaDocument.ystate) when it has one: every reload and every device then
 *    rebuilds identical Yjs structs, so merging with whatever a peer later
 *    delivers is idempotent — the "both sides seeded independently → document
 *    duplicated" race cannot occur, no matter how slow WebRTC/signaling is.
 *    Only legacy docs without ystate still take the seed path (wait briefly
 *    for a peer, seed from the content snapshot if the room stays empty).
 *    There's still no y-indexeddb — ystate lives INSIDE document.json, so it
 *    travels atomically with the snapshot through cloud sync's newer-wins
 *    mtime comparison; a separately-stale local Yjs state can't exist.
 *  - `provider` is null when no signaling URL is configured (or no project key):
 *    the editor still runs on Yjs locally (undo via the Yjs UndoManager), just
 *    without peers.
 *
 * Sessions register themselves by fileId so tabs.reloadActiveDocumentFromDisk
 * can find the live session for the open document (see getCollabSession).
 */
import * as Y from "yjs";
import { WebrtcProvider } from "y-webrtc";
import { getEditorSchema } from "../editor/schema";
import { deriveRoom } from "./room";
import {
  fragmentIsEmpty,
  seedYDocFromContent,
  clearFragment,
  COLLAB_FIELD,
  SEED_ORIGIN,
  applyEncodedDocState,
  decodeDocState,
  updateSharesLineage,
} from "./seed";
import { createHighlightsBridge, HighlightsBridge } from "./highlightsBridge";
import { SignalingConfig } from "./signalingConfig";
import {
  getSignalingKey,
  appendKeyToUrls,
  SignalingNotAuthorizedError,
} from "./signalingAuth";
import { resolveCollabIdentity } from "./identity";

export interface CollabUser {
  name: string;
  color: string;
}

export interface CollabSession {
  fileId: string;
  ydoc: Y.Doc;
  provider: WebrtcProvider | null;
  user: CollabUser;
  /** Unix-seconds expiry of the signaling key in use, or null when none was
   *  minted (collaboration off, or a tokenless direct connection). Drives the
   *  hook's pre-expiry rebuild. */
  signalingKeyExpiresAt: number | null;
  /** Why the WebRTC provider isn't running, when it isn't: "unauthorized" for a
   *  403 from /register (not allowlisted), "failed" for any other setup error.
   *  Null when the provider is running or collaboration wasn't configured. */
  signalingError: "unauthorized" | "failed" | null;
  /** Bumped by the hook across rebuilds so the editor remounts cleanly (rebinds
   *  CollaborationCursor to the fresh provider). Set by useCollabSession. */
  epoch?: number;
  /** True while at least one other collaborator is connected in this room. */
  hasPeers(): boolean;
  /** Adopt a newer snapshot pulled from the cloud for the open document (no
   *  peers present). When the snapshot carries a ystate sharing Yjs lineage
   *  with the live doc this is a true CRDT merge; otherwise the content is
   *  replaced (clear + seed). */
  reseed(contentJSON: unknown, ystate?: string | null): void;
  destroy(): void;
}

export interface BuildSessionOpts {
  fileId: string;
  projectName: string | null;
  roomSecret: string | null;
  /** Effective signaling config for the active mode, or null when off. */
  signaling: SignalingConfig | null;
  contentJSON: unknown;
  /** Persisted Yjs state to restore (QdaDocument.ystate), if the snapshot has one. */
  ystate: string | null;
  user: CollabUser;
  /** Extra iceServers (STUN+TURN) for WebrtcProvider's peerOpts. Empty = use
   *  simple-peer's built-in STUN-only default (peerOpts omitted). */
  iceServers: RTCIceServer[];
}

/**
 * Ref-counted session registry keyed by fileId. Two things depend on it:
 *  - tabs.reloadActiveDocumentFromDisk finds the live session (getCollabSession).
 *  - React StrictMode (dev) double-invokes effects: mount → unmount → mount.
 *    With async session setup, the unmount's cleanup fires before the first
 *    build finishes, so it can't tear it down, and a naive remount would build
 *    a SECOND WebrtcProvider for the same room — which y-webrtc forbids
 *    ("A Yjs Doc connected to room X already exists!"). Ref-counting + a
 *    deferred teardown makes the remount reuse the same session instead.
 */
interface SessionEntry {
  fileId: string;
  promise: Promise<CollabSession>;
  session: CollabSession | null;
  refs: number;
  teardownTimer: ReturnType<typeof setTimeout> | null;
}

const entries = new Map<string, SessionEntry>();

/** The live session for a document, if one is currently mounted. */
export function getCollabSession(fileId: string): CollabSession | undefined {
  return entries.get(fileId)?.session ?? undefined;
}

/**
 * Get (or start building) the shared session for a document and add a
 * reference. Every acquire must be paired with a releaseCollabSession.
 */
export function acquireCollabSession(opts: BuildSessionOpts): Promise<CollabSession> {
  const { fileId } = opts;
  const existing = entries.get(fileId);
  if (existing) {
    if (existing.teardownTimer) {
      clearTimeout(existing.teardownTimer);
      existing.teardownTimer = null;
    }
    existing.refs++;
    return existing.promise;
  }
  const entry: SessionEntry = {
    fileId,
    promise: undefined as unknown as Promise<CollabSession>,
    session: null,
    refs: 1,
    teardownTimer: null,
  };
  entry.promise = buildCollabSession(opts).then((s) => {
    entry.session = s;
    return s;
  });
  entries.set(fileId, entry);
  return entry.promise;
}

/**
 * Force-destroy the live session for a document regardless of refs, so the next
 * acquire rebuilds it from scratch. Used by the collab hook to recover a dead /
 * expired signaling key with a fresh WebrtcProvider (the normal ref-counted
 * teardown would instead *reuse* the existing session). Data is safe — the
 * durable snapshot is untouched and the rebuilt Y.Doc reseeds from it / peers.
 */
export function dropCollabSession(fileId: string): void {
  const entry = entries.get(fileId);
  if (!entry) return;
  if (entry.teardownTimer) {
    clearTimeout(entry.teardownTimer);
    entry.teardownTimer = null;
  }
  entries.delete(fileId);
  entry.promise.then((s) => s.destroy()).catch(() => {});
}

/** Drop a reference; tears the session down once nothing holds it. */
export function releaseCollabSession(fileId: string): void {
  const entry = entries.get(fileId);
  if (!entry) return;
  entry.refs--;
  if (entry.refs > 0) return;
  // Defer so a StrictMode unmount immediately followed by a remount reuses the
  // session (the remount's acquire clears this timer) instead of destroying and
  // recreating the WebRTC room.
  entry.teardownTimer = setTimeout(() => {
    if (entry.refs > 0) return;
    entries.delete(fileId);
    entry.promise.then((s) => s.destroy()).catch(() => {});
  }, 0);
}

/**
 * Turns the effective signaling config into the `signaling` array for
 * WebrtcProvider, minting an access key when a token is configured.
 *  - No token → a BYO user pointing at a tokenless/vanilla signaling server:
 *    connect directly, no /register (never require registration when there's no
 *    token). No key, so no expiry.
 *  - Token → obtain a verified id, mint one key (valid on all hosts), and append
 *    `?token=<key>` to every URL.
 */
async function resolveSignalingUrls(
  cfg: SignalingConfig
): Promise<{ urls: string[]; expiresAt: number | null }> {
  if (!cfg.token) return { urls: cfg.urls, expiresAt: null };
  const id = await resolveCollabIdentity();
  const { key, expiresAt } = await getSignalingKey({ urls: cfg.urls, token: cfg.token, id });
  return { urls: appendKeyToUrls(cfg.urls, key), expiresAt };
}

async function buildCollabSession(opts: BuildSessionOpts): Promise<CollabSession> {
  const { fileId, projectName, roomSecret, signaling, contentJSON, ystate, user, iceServers } = opts;
  const schema = getEditorSchema();
  const ydoc = new Y.Doc();

  // Restore the persisted Yjs state FIRST (before the provider exists), so the
  // initial sync protocol exchanges real state vectors: peers send us only what
  // we're missing, and our (identical-lineage) structs dedupe on their side.
  const restored = ystate ? applyEncodedDocState(ydoc, ystate) : false;

  let provider: WebrtcProvider | null = null;
  let signalingKeyExpiresAt: number | null = null;
  let signalingError: "unauthorized" | "failed" | null = null;
  if (signaling && signaling.urls.length > 0 && projectName && roomSecret) {
    try {
      const { room, password } = await deriveRoom(projectName, fileId, roomSecret);
      const resolved = await resolveSignalingUrls(signaling);
      // Don't connect with no key when one was required — the server would just
      // reject the WS. Surface the failure instead and run locally.
      provider = new WebrtcProvider(room, ydoc, {
        signaling: resolved.urls,
        password,
        ...(iceServers.length > 0 ? { peerOpts: { config: { iceServers } } } : {}),
      });
      signalingKeyExpiresAt = resolved.expiresAt;
    } catch (err) {
      if (err instanceof SignalingNotAuthorizedError) {
        signalingError = "unauthorized";
        console.warn("[collab] not authorized for collaboration:", err.message);
      } else {
        signalingError = "failed";
        console.warn("[collab] signaling setup failed; running locally:", err);
      }
      provider = null;
    }
  }

  // Initial content. Restored-from-ystate docs are ready NOW: their structs
  // share lineage with every peer's, so late-arriving peer state merges
  // idempotently — no waiting, no occupancy guessing. Only legacy docs
  // (no ystate yet) must still play the seed-vs-adopt game: wait for a peer
  // to deliver content, and seed from the snapshot only if the room stays
  // empty — see waitForInitialContent.
  if (provider && !restored) {
    await waitForInitialContent(provider, ydoc);
  }
  if (fragmentIsEmpty(ydoc)) {
    seedYDocFromContent(ydoc, contentJSON, schema);
  }

  if (provider) {
    provider.awareness.setLocalStateField("user", { name: user.name, color: user.color });
  }

  const bridge: HighlightsBridge = createHighlightsBridge(ydoc);

  const session: CollabSession = {
    fileId,
    ydoc,
    provider,
    user,
    signalingKeyExpiresAt,
    signalingError,
    hasPeers() {
      return provider ? provider.awareness.getStates().size > 1 : false;
    },
    reseed(json: unknown, remoteYstate?: string | null) {
      // Prefer a true CRDT merge: if the downloaded snapshot's Yjs state shares
      // lineage with the live doc (or the doc is still empty), applying it
      // dedupes common history and interleaves what's new — even edits made
      // here that lost the mtime race survive. Clear+seed (replace semantics)
      // only remains for lineage-less snapshots, where a merge would
      // concatenate two independent copies.
      const update = remoteYstate ? decodeDocState(remoteYstate) : null;
      if (update && (fragmentIsEmpty(ydoc) || updateSharesLineage(ydoc, update))) {
        Y.applyUpdate(ydoc, update, SEED_ORIGIN);
        return;
      }
      clearFragment(ydoc);
      seedYDocFromContent(ydoc, json, schema);
    },
    destroy() {
      bridge.destroy();
      if (provider) {
        try {
          provider.destroy();
        } catch {
          /* provider may already be torn down */
        }
      }
      ydoc.destroy();
    },
  };
  return session;
}

/**
 * Decide when a freshly-created Y.Doc may be seeded from the disk snapshot.
 * LEGACY-ONLY: applies to documents saved before QdaDocument.ystate existed
 * (restored docs skip this entirely — their merge is timing-independent).
 * Inherently a heuristic: P2P has no authority to ask "is anyone here?", so
 * this can only shorten the race window, never close it. The real fix is
 * ystate; a legacy doc graduates to it on its first edit.
 *
 * Seeding is only safe when we're genuinely first into the room. If a peer is
 * present we must adopt THEIR live state instead: seeding alongside a peer
 * duplicates the document, because the peer delivers its full copy and — once
 * anyone has edited, so the content isn't byte-identical to our snapshot — Yjs
 * merges it next to our seed rather than recognizing it as the same content.
 *
 * The old fixed 800ms wait lost this race in production. A real WebRTC peer's
 * data channel routinely takes longer than 800ms to open (STUN/ICE/DTLS over
 * the internet), so the reloading peer seeded before the peer synced. On
 * localhost peers sync over BroadcastChannel (same-origin tabs) or a near-
 * instant LAN hop, well under 800ms — which is exactly why duplication only
 * showed up once deployed.
 *
 * So instead of a blind timer we watch what the provider is actually doing and
 * resolve as soon as ANY of these holds:
 *  - the fragment becomes non-empty — a peer delivered content, adopt it;
 *  - no peer is present and we've either been connected to signaling for a
 *    grace window or waited out a no-signaling fallback — we're alone, seed;
 *  - a hard cap elapses (a peer is present but never syncs) — seed as a last
 *    resort so the editor is never stuck on "Connecting…". Deterministic
 *    seeding keeps even this path duplication-free when the peer seeded the
 *    identical snapshot.
 */
function waitForInitialContent(provider: WebrtcProvider, ydoc: Y.Doc): Promise<void> {
  const POLL_MS = 150;
  // Floor: even when signaling connects instantly, give peers a beat to announce
  // before we could conclude we're alone.
  const MIN_WAIT_MS = 400;
  // Once connected to signaling with no peer in sight this long, treat as alone.
  // Must comfortably exceed a peer's announce round-trip after our WS connects
  // (sub-second on a healthy server) so a present peer is detected first.
  const POST_CONNECT_GRACE_MS = 1000;
  // No peer AND signaling never came up this long → alone (server down / off).
  const NO_PEERS_FALLBACK_MS = 3000;
  // Peer present but never syncs → seed anyway rather than hang forever.
  const HARD_CAP_MS = 8000;

  return new Promise((resolve) => {
    const frag = ydoc.getXmlFragment(COLLAB_FIELD);
    const start = Date.now();
    let done = false;
    let signalingConnectedAt: number | null = null;

    // Connection-based presence is the reliable EARLY signal: a WebrtcConn is
    // created (and bcConns populated) at signaling/BroadcastChannel announce
    // time — before the slow RTC data channel opens — so we detect a peer well
    // ahead of its content arriving. (Awareness isn't usable here: our own local
    // awareness state isn't set until after this wait, so its count is skewed.)
    const hasPeers = (): boolean => {
      const room = provider.room;
      return !!room && (room.webrtcConns.size > 0 || room.bcConns.size > 0);
    };
    const signalingConnected = (): boolean =>
      provider.signalingConns.some((c) => c && c.connected);

    const finish = () => {
      if (done) return;
      done = true;
      frag.unobserve(onFragment);
      clearInterval(timer);
      resolve();
    };
    const onFragment = () => {
      if (frag.length > 0) finish();
    };
    frag.observe(onFragment);
    if (frag.length > 0) return finish();

    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      if (frag.length > 0) return finish(); // a peer delivered content → adopt
      if (elapsed >= HARD_CAP_MS) return finish(); // give up waiting → seed
      if (elapsed < MIN_WAIT_MS) return;
      if (hasPeers()) return; // peer present/connecting → wait for its state
      if (signalingConnectedAt === null && signalingConnected()) {
        signalingConnectedAt = Date.now();
      }
      const graceMet =
        signalingConnectedAt !== null && Date.now() - signalingConnectedAt >= POST_CONNECT_GRACE_MS;
      if (graceMet || elapsed >= NO_PEERS_FALLBACK_MS) finish(); // alone → seed
    }, POLL_MS);
  });
}
